import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Table, Badge, Modal, Alert } from 'react-bootstrap';
import { Plus, Check, X, Eye, Calendar } from 'lucide-react';
import { getLeaveRequests, approveLeaveRequest, rejectLeaveRequest, createLeaveRequest, cancelLeaveRequest, getLeaveBalances, getAllEmployeeBalances } from '../../components/api/LeaveApi';
import useAuth from '../useAuth';

const LeaveRequests = () => {
     const [requests, setRequests] = useState([]);
     const [balances, setBalances] = useState([]);
     const [loading, setLoading] = useState(true);
     const [showModal, setShowModal] = useState(false);
     const [showDetailsModal, setShowDetailsModal] = useState(false);
     const [selectedRequest, setSelectedRequest] = useState(null);
     const [message, setMessage] = useState({ type: '', text: '' });
     const [filterStatus, setFilterStatus] = useState('all');
     const [selectedEmployeeBalances, setSelectedEmployeeBalances] = useState([]);
     const [cancelReasonError, setCancelReasonError] = useState('');
     const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

     const { getIsLeaveApplicable } = useAuth();
     const userRole = sessionStorage.getItem('user_role');
     const loggedInEmpCode = sessionStorage.getItem('user_code');
     const isLeaveApplicable = getIsLeaveApplicable();

     const [formData, setFormData] = useState({
          leave_type: isLeaveApplicable ? 'PL' : 'LOP',
          from_date: '',
          to_date: '',
          reason: '',
          multiple_days: false
     });

     useEffect(() => {
          const handleResize = () => setIsMobile(window.innerWidth < 768);
          window.addEventListener('resize', handleResize);
          return () => window.removeEventListener('resize', handleResize);
     }, []);

     useEffect(() => {
          fetchRequests();
          if (isLeaveApplicable) {
               fetchBalances();
          }
     }, [filterStatus]);

     const fetchEmployeeBalances = async (empCode) => {
          try {
               const currentDate = new Date();
               const currentMonth = currentDate.getMonth() + 1;
               const currentYear = currentDate.getFullYear();

               const fyStartYear = currentMonth >= 4 ? currentYear : currentYear - 1;
               const financialYear = `${fyStartYear}-${fyStartYear + 1}`;

               const data = await getLeaveBalances(empCode, financialYear);
               let balancesData = [];
               if (Array.isArray(data)) {
                    balancesData = data;
               } else if (data && data.data && Array.isArray(data.data)) {
                    balancesData = data.data;
               } else if (data && typeof data === 'object' && !data.error) {
                    balancesData = [data];
               }

               setSelectedEmployeeBalances(balancesData);
          } catch (error) {
               console.error('❌ Failed to fetch employee balances:', error);
               setSelectedEmployeeBalances([]);
          }
     };

     const fetchBalances = async () => {
          try {
               const currentDate = new Date();
               const currentMonth = currentDate.getMonth() + 1;
               const currentYear = currentDate.getFullYear();

               const fyStartYear = currentMonth >= 4 ? currentYear : currentYear - 1;
               const fyEndYear = fyStartYear + 1;
               const financialYear = `${fyStartYear}-${fyEndYear}`;

               let data;
               if (loggedInEmpCode) {
                    data = await getLeaveBalances(loggedInEmpCode, financialYear);
               } else {
                    setBalances([]);
                    return;
               }

               let balancesData = [];
               if (Array.isArray(data)) {
                    balancesData = data;
               } else if (data && data.data && Array.isArray(data.data)) {
                    balancesData = data.data;
               } else if (data && typeof data === 'object' && !data.error) {
                    balancesData = [data];
               }

               setBalances(balancesData);
          } catch (error) {
               console.error('❌ Failed to fetch balances:', error);
               setBalances([]);
          }
     };

     const handleViewDetails = async (request) => {
          setSelectedRequest(request);
          setShowDetailsModal(true);
          if (request.emp_code) {
               await fetchEmployeeBalances(request.emp_code);
          }
     };

     const fetchRequests = async () => {
          try {
               setLoading(true);
               setMessage({ type: '', text: '' });

               const filters = {};
               if (userRole === 'EMPLOYEE') {
                    filters.emp_code = loggedInEmpCode;
               }
               if (filterStatus !== 'all') {
                    filters.status = filterStatus;
               }

               const data = await getLeaveRequests(filters);
               const requestsData = Array.isArray(data) ? data : data.data || [];
               setRequests(requestsData);
          } catch (error) {
               console.error('Fetch error:', error);
               setMessage({ type: 'danger', text: 'Failed to fetch leave requests' });
               setRequests([]);
          } finally {
               setLoading(false);
          }
     };

     const calculateDays = (fromDate, toDate) => {
          if (!fromDate || !toDate) return 0;
          const start = new Date(fromDate);
          const end = new Date(toDate);
          const diffTime = Math.abs(end - start);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
          return diffDays;
     };

     const handleSubmit = async (e) => {
          e.preventDefault();
          setLoading(true);
          setMessage({ type: '', text: '' });

          const days = formData.multiple_days
               ? calculateDays(formData.from_date, formData.to_date)
               : 1;

          if (isLeaveApplicable && formData.leave_type !== 'LOP') {
               const balance = balances.find(b => b.leave_type === formData.leave_type);
               if (!balance || balance.remaining_leaves < days) {
                    setMessage({ type: 'danger', text: 'Insufficient leave balance!' });
                    setLoading(false);
                    return;
               }
          }

          try {
               const requestData = {
                    emp_code: loggedInEmpCode,
                    leave_type: formData.leave_type,
                    start_date: formData.from_date,
                    end_date: formData.multiple_days ? formData.to_date : formData.from_date,
                    from_date: formData.from_date,
                    to_date: formData.multiple_days ? formData.to_date : formData.from_date,
                    days: days,
                    days_requested: days,
                    total_days: days,
                    reason: formData.reason,
                    multiple_days: formData.multiple_days
               };

               await createLeaveRequest(requestData);
               setMessage({ type: 'success', text: 'Leave request submitted successfully!' });
               setFormData({
                    leave_type: isLeaveApplicable ? 'PL' : 'LOP',
                    from_date: '',
                    to_date: '',
                    reason: '',
                    multiple_days: false
               });
               setTimeout(() => {
                    setShowModal(false);
                    fetchRequests();
               }, 1500);
          } catch (error) {
               setMessage({ type: 'danger', text: error.message || 'Failed to submit request' });
          } finally {
               setLoading(false);
          }
     };

     const handleApprove = async (requestId, remarks = '') => {
          try {
               await approveLeaveRequest(requestId, remarks);
               setMessage({ type: 'success', text: 'Leave request approved!' });
               fetchRequests();
               setShowDetailsModal(false);
          } catch (error) {
               setMessage({ type: 'danger', text: 'Failed to approve request' });
          }
     };

     const handleReject = async (requestId, remarks) => {
          if (!remarks) {
               setMessage({ type: 'warning', text: 'Please provide rejection reason' });
               return;
          }
          try {
               await rejectLeaveRequest(requestId, remarks);
               setMessage({ type: 'success', text: 'Leave request rejected!' });
               fetchRequests();
               setShowDetailsModal(false);
          } catch (error) {
               setMessage({ type: 'danger', text: 'Failed to reject request' });
          }
     };

     const handleCancel = async (requestId, reason) => {
          if (!reason) {
               setCancelReasonError('Please provide cancellation reason');
               return;
          }

          setCancelReasonError('');

          try {
               await cancelLeaveRequest(requestId, reason);
               setMessage({ type: 'success', text: 'Leave request cancelled!' });
               fetchRequests();
               setShowDetailsModal(false);
          } catch (error) {
               setMessage({ type: 'danger', text: 'Failed to cancel request' });
          }
     };

     const getStatusBadge = (status) => {
          const statusLower = status?.toLowerCase() || '';
          const variants = {
               'pending': 'warning',
               'approved': 'success',
               'rejected': 'danger',
               'cancelled': 'secondary'
          };
          return <Badge bg={variants[statusLower] || 'secondary'}>{status?.toUpperCase() || 'N/A'}</Badge>;
     };

     const canApproveReject = (request) => {
          return (userRole === 'HR' || userRole === 'SUPER_ADMIN' || userRole === 'admin' || request.reporting_manager === loggedInEmpCode) && request.status?.toLowerCase() === 'pending';
     };

     const canCancel = (request) => {
          return request.emp_code === loggedInEmpCode && request.status?.toLowerCase() === 'pending';
     };

     const formatDate = (dateString) => {
          if (!dateString) return 'N/A';
          try {
               const date = new Date(dateString);
               if (isNaN(date.getTime())) return 'N/A';
               return date.toLocaleDateString('en-GB');
          } catch (error) {
               return 'N/A';
          }
     };

     const getFromDate = (request) => {
          return request.from_date || request.start_date;
     };

     const getToDate = (request) => {
          return request.to_date || request.end_date;
     };

     // Mobile Card Component


     const MobileRequestCard = ({ request }) => (
          <Card className="mb-2" style={{ fontSize: '13px', borderRadius: '8px' }}>
               <Card.Body style={{ padding: '12px' }}>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                         <div>
                              <strong style={{ fontSize: '14px' }}>{request.emp_code}</strong>
                              <div className="text-muted small">{request.employee_name || 'N/A'}</div>
                         </div>
                         {getStatusBadge(request.status)}
                    </div>

                    <Row className="g-2 mb-2">
                         <Col xs={6}>
                              <small className="text-muted d-block" style={{ fontSize: '11px' }}>From Date</small>
                              <strong style={{ fontSize: '13px' }}>{formatDate(getFromDate(request))}</strong>
                         </Col>
                         <Col xs={6}>
                              <small className="text-muted d-block" style={{ fontSize: '11px' }}>To Date</small>
                              <strong style={{ fontSize: '13px' }}>{formatDate(getToDate(request))}</strong>
                         </Col>
                         <Col xs={6}>
                              <small className="text-muted d-block" style={{ fontSize: '11px' }}>Leave Type</small>
                              <Badge bg="info" style={{ fontSize: '11px' }}>{request.leave_type || 'N/A'}</Badge>
                         </Col>
                         <Col xs={6}>
                              <small className="text-muted d-block" style={{ fontSize: '11px' }}>Days</small>
                              <strong style={{ fontSize: '13px' }}>{request.days_requested || request.days || calculateDays(getFromDate(request), getToDate(request)) || 0}</strong>
                         </Col>
                    </Row>

                    <div className="d-flex gap-2 justify-content-end mt-2">
                         {canApproveReject(request) && (
                              <>
                                   <Button
                                        size="sm"
                                        variant="success"
                                        onClick={() => handleApprove(request.id)}
                                        style={{
                                             fontSize: '12px',
                                             padding: '6px 12px',
                                             borderRadius: '6px',
                                             minWidth: 'auto'
                                        }}
                                   >
                                        <Check size={16} />
                                   </Button>
                                   <Button
                                        size="sm"
                                        variant="danger"
                                        onClick={() => handleViewDetails(request)}
                                        style={{
                                             fontSize: '12px',
                                             padding: '6px 12px',
                                             borderRadius: '6px',
                                             minWidth: 'auto'
                                        }}
                                   >
                                        <X size={16} />
                                   </Button>
                              </>
                         )}
                         {!canApproveReject(request) && (
                              <Button
                                   size="sm"
                                   variant="outline-primary"
                                   onClick={() => handleViewDetails(request)}
                                   style={{
                                        fontSize: '12px',
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        minWidth: 'auto'
                                   }}
                              >
                                   <Eye size={16} />
                              </Button>
                         )}
                    </div>
               </Card.Body>
          </Card>
     );

     return (
          <div className="leave-requests-section" style={{ padding: isMobile ? '12px' : '24px' }}>
               <Row className="mb-3">
                    <Col xs={12} md={4} className="mb-2 mb-md-0">
                         <h4 style={{ fontSize: isMobile ? '18px' : '24px' }}>Leave Requests</h4>
                    </Col>
                    <Col xs={12} md={4} className="mb-2 mb-md-0">
                         <Form.Select
                              value={filterStatus}
                              onChange={(e) => setFilterStatus(e.target.value)}
                              size="sm"
                              style={{ fontSize: isMobile ? '13px' : '14px' }}
                         >
                              <option value="all">All Requests</option>
                              <option value="pending">Pending</option>
                              <option value="approved">Approved</option>
                              <option value="rejected">Rejected</option>
                              <option value="cancelled">Cancelled</option>
                         </Form.Select>
                    </Col>
                    <Col xs={12} md={4} className="text-md-end">
                         <Button
                              variant="primary"
                              size="sm"
                              onClick={() => setShowModal(true)}
                              className="w-100 w-md-auto"
                              style={{ fontSize: isMobile ? '13px' : '14px' }}
                         >
                              <Plus size={16} className="me-1" />
                              New Request
                         </Button>
                    </Col>
               </Row>

               {message.text && (
                    <Alert
                         variant={message.type}
                         onClose={() => setMessage({ type: '', text: '' })}
                         dismissible
                         style={{ fontSize: isMobile ? '13px' : '14px' }}
                    >
                         {message.text}
                    </Alert>
               )}

               {/* Desktop Table / Mobile Cards */}
               {isMobile ? (
                    <div>
                         {loading ? (
                              <div className="text-center py-4">
                                   <div className="spinner-border spinner-border-sm text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                   </div>
                              </div>
                         ) : requests.length === 0 ? (
                              <Card className="text-center py-5">
                                   <Card.Body>
                                        <Calendar size={48} className="text-muted mb-3" />
                                        <h6 className="text-muted">No leave requests found</h6>
                                   </Card.Body>
                              </Card>
                         ) : (
                              requests.map((request) => (
                                   <MobileRequestCard key={request.id} request={request} />
                              ))
                         )}
                    </div>
               ) : (
                    <Card>
                         <Card.Body className="p-0">
                              <div className="table-responsive">
                                   <Table hover className="mb-0">
                                        <thead className="table-light">
                                             <tr>
                                                  <th>Employee Code</th>
                                                  <th>Employee Name</th>
                                                  <th>From Date</th>
                                                  <th>To Date</th>
                                                  <th>Leave Type</th>
                                                  <th>Days</th>
                                                  <th>Status</th>
                                                  <th>Applied On</th>
                                                  <th>Actions</th>
                                             </tr>
                                        </thead>
                                        <tbody>
                                             {loading ? (
                                                  <tr>
                                                       <td colSpan="9" className="text-center py-4">
                                                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                                 <span className="visually-hidden">Loading...</span>
                                                            </div>
                                                       </td>
                                                  </tr>
                                             ) : requests.length === 0 ? (
                                                  <tr>
                                                       <td colSpan="9" className="text-center py-5">
                                                            <Calendar size={48} className="text-muted mb-3" />
                                                            <h6 className="text-muted">No leave requests found</h6>
                                                       </td>
                                                  </tr>
                                             ) : (
                                                  requests.map((request) => (
                                                       <tr key={request.id}>
                                                            <td><strong>{request.emp_code}</strong></td>
                                                            <td>{request.employee_name || 'N/A'}</td>
                                                            <td>{formatDate(getFromDate(request))}</td>
                                                            <td>{formatDate(getToDate(request))}</td>
                                                            <td>{request.leave_type || request.type || 'N/A'}</td>
                                                            <td><strong>{request.days_requested || request.days || calculateDays(getFromDate(request), getToDate(request)) || 0}</strong></td>
                                                            <td>{getStatusBadge(request.status)}</td>
                                                            <td>{formatDate(request.created_at || request.applied_on)}</td>
                                                            <td>
                                                                 <div className="d-flex gap-2 justify-content-center align-items-center">
                                                                      <button
                                                                           type="button"
                                                                           className="btn btn-link p-0 border-0 text-primary"
                                                                           onClick={() => handleViewDetails(request)}
                                                                           style={{ background: 'none', minWidth: 'auto' }}
                                                                           title="View Details"
                                                                      >
                                                                           <Eye size={18} />
                                                                      </button>
                                                                      {canApproveReject(request) && (
                                                                           <>
                                                                                <button
                                                                                     type="button"
                                                                                     className="btn btn-link p-0 border-0 text-success"
                                                                                     onClick={() => handleApprove(request.id)}
                                                                                     style={{ background: 'none', minWidth: 'auto' }}
                                                                                     title="Approve"
                                                                                >
                                                                                     <Check size={18} />
                                                                                </button>
                                                                                <button
                                                                                     type="button"
                                                                                     className="btn btn-link p-0 border-0 text-danger"
                                                                                     onClick={() => handleViewDetails(request)}
                                                                                     style={{ background: 'none', minWidth: 'auto' }}
                                                                                     title="Reject"
                                                                                >
                                                                                     <X size={18} />
                                                                                </button>
                                                                           </>
                                                                      )}
                                                                 </div>
                                                            </td>
                                                       </tr>
                                                  ))
                                             )}
                                        </tbody>
                                   </Table>
                              </div>
                         </Card.Body>
                    </Card>
               )}

               {/* New Request Modal */}
               <Modal
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    size="lg"
                    fullscreen={isMobile ? 'sm-down' : false}
               >
                    <Modal.Header closeButton>
                         <Modal.Title style={{ fontSize: isMobile ? '16px' : '20px' }}>New Leave Request</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className='m-4' style={{ padding: isMobile ? '12px' : '16px' }}>
                         <Form onSubmit={handleSubmit}>
                              <Row>
                                   {isLeaveApplicable && (
                                        <>
                                             <Col xs={12} md={6}>
                                                  <Form.Group className="mb-3">
                                                       <Form.Label style={{ fontSize: isMobile ? '13px' : '14px' }}>Leave Type</Form.Label>
                                                       <Form.Select
                                                            value={formData.leave_type}
                                                            onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })}
                                                            required
                                                            size={isMobile ? 'sm' : 'md'}
                                                       >
                                                            <option value="PL">Privilege Leave (PL)</option>
                                                            <option value="CL">Casual Leave (CL)</option>
                                                            <option value="SL">Sick Leave (SL)</option>
                                                            <option value="LOP">Loss of Pay (LOP)</option>
                                                       </Form.Select>
                                                       {formData.leave_type !== 'LOP' && !balances.find(b => b.leave_type === formData.leave_type) && (
                                                            <Form.Text className="text-danger" style={{ fontSize: '11px' }}>
                                                                 ⚠️ No balance allocated for {formData.leave_type}
                                                            </Form.Text>
                                                       )}
                                                  </Form.Group>
                                             </Col>
                                             <Col xs={12} md={6}>
                                                  <Form.Group className="mb-3">
                                                       <Form.Label style={{ fontSize: isMobile ? '13px' : '14px' }}>Available Balance</Form.Label>
                                                       <Form.Control
                                                            type="text"
                                                            value={
                                                                 formData.leave_type === 'LOP'
                                                                      ? 'Unlimited'
                                                                      : (balances.find(b => b.leave_type === formData.leave_type)?.remaining_leaves || '0 (Not Allocated)')
                                                            }
                                                            disabled
                                                            size={isMobile ? 'sm' : 'md'}
                                                       />
                                                  </Form.Group>
                                             </Col>
                                        </>
                                   )}
                              </Row>

                              <Row>
                                   <Col xs={12} md={6}>
                                        <Form.Group className="mb-3">
                                             <Form.Label style={{ fontSize: isMobile ? '13px' : '14px' }}>From Date</Form.Label>
                                             <Form.Control
                                                  type="date"
                                                  value={formData.from_date}
                                                  onChange={(e) => setFormData({ ...formData, from_date: e.target.value })}
                                                  required
                                                  size={isMobile ? 'sm' : 'md'}
                                             />
                                        </Form.Group>
                                   </Col>
                                   <Col xs={12} md={6}>
                                        <Form.Group className="mb-3">
                                             <Form.Label style={{ fontSize: isMobile ? '13px' : '14px' }}>To Date</Form.Label>
                                             <Form.Control
                                                  type="date"
                                                  value={formData.to_date}
                                                  onChange={(e) => setFormData({ ...formData, to_date: e.target.value })}
                                                  min={formData.from_date}
                                                  required={formData.multiple_days}
                                                  disabled={!formData.multiple_days}
                                                  size={isMobile ? 'sm' : 'md'}
                                             />
                                        </Form.Group>
                                   </Col>
                              </Row>

                              <Form.Group className="mb-3">
                                   <Form.Check
                                        type="checkbox"
                                        label="Multiple Days"
                                        checked={formData.multiple_days}
                                        onChange={(e) => setFormData({
                                             ...formData,
                                             multiple_days: e.target.checked,
                                             to_date: e.target.checked ? formData.to_date : formData.from_date
                                        })}
                                        style={{ fontSize: isMobile ? '13px' : '14px' }}
                                   />
                              </Form.Group>

                              {formData.from_date && formData.to_date && formData.multiple_days && (
                                   <Alert variant="info" style={{ fontSize: isMobile ? '13px' : '14px', padding: isMobile ? '8px 12px' : '12px' }}>
                                        Total Days: <strong>{calculateDays(formData.from_date, formData.to_date)}</strong>
                                   </Alert>
                              )}

                              <Form.Group className="mb-3">
                                   <Form.Label style={{ fontSize: isMobile ? '13px' : '14px' }}>Reason</Form.Label>
                                   <Form.Control
                                        as="textarea"
                                        rows={isMobile ? 2 : 3}
                                        value={formData.reason}
                                        onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                                        placeholder="Enter reason for leave"
                                        required
                                        size={isMobile ? 'sm' : 'md'}
                                   />
                              </Form.Group>

                              <div className="d-flex justify-content-end gap-2">
                                   <Button
                                        variant="secondary"
                                        onClick={() => setShowModal(false)}
                                        size={isMobile ? 'sm' : 'md'}
                                        style={{ fontSize: isMobile ? '13px' : '14px' }}
                                   >
                                        Cancel
                                   </Button>
                                   <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={loading}
                                        size={isMobile ? 'sm' : 'md'}
                                        style={{ fontSize: isMobile ? '13px' : '14px' }}
                                   >
                                        {loading ? 'Submitting...' : 'Submit Request'}
                                   </Button>
                              </div>
                         </Form>
                    </Modal.Body>
               </Modal>

               {/* Request Details Modal */}
               <Modal
                    show={showDetailsModal}
                    onHide={() => setShowDetailsModal(false)}
                    size="lg"
                    fullscreen={isMobile ? 'sm-down' : false}
               >
                    <Modal.Header closeButton>
                         <Modal.Title style={{ fontSize: isMobile ? '16px' : '20px' }}>Leave Request Details</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className='m-2' style={{ padding: isMobile ? '12px' : '16px' }}>
                         {selectedRequest && (
                              <>
                                   {/* Compact Header */}
                                   <div className="mb-3">
                                        <Row className="mb-2 g-2">
                                             <Col xs={6} md={3}>
                                                  <small className="text-muted d-block" style={{ fontSize: isMobile ? '11px' : '12px' }}>Employee</small>
                                                  <strong style={{ fontSize: isMobile ? '13px' : '14px' }}>{selectedRequest.emp_code}</strong>
                                                  <div className="text-muted small" style={{ fontSize: isMobile ? '11px' : '12px' }}>{selectedRequest.employee_name}</div>
                                             </Col>
                                             <Col xs={6} md={3}>
                                                  <small className="text-muted d-block" style={{ fontSize: isMobile ? '11px' : '12px' }}>Leave Type</small>
                                                  <Badge bg="primary" className="mt-1" style={{ fontSize: isMobile ? '11px' : '12px' }}>{selectedRequest.leave_type}</Badge>
                                             </Col>
                                             <Col xs={6} md={3}>
                                                  <small className="text-muted d-block" style={{ fontSize: isMobile ? '11px' : '12px' }}>Status</small>
                                                  <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
                                             </Col>
                                             <Col xs={6} md={3}>
                                                  <small className="text-muted d-block" style={{ fontSize: isMobile ? '11px' : '12px' }}>Applied On</small>
                                                  <strong style={{ fontSize: isMobile ? '13px' : '14px' }}>{formatDate(selectedRequest.applied_on || selectedRequest.created_at)}</strong>
                                             </Col>
                                        </Row>
                                        <Row className="g-2">
                                             <Col xs={6} md={3}>
                                                  <small className="text-muted d-block" style={{ fontSize: isMobile ? '11px' : '12px' }}>From Date</small>
                                                  <strong style={{ fontSize: isMobile ? '13px' : '14px' }}>{formatDate(getFromDate(selectedRequest))}</strong>
                                             </Col>
                                             <Col xs={6} md={3}>
                                                  <small className="text-muted d-block" style={{ fontSize: isMobile ? '11px' : '12px' }}>To Date</small>
                                                  <strong style={{ fontSize: isMobile ? '13px' : '14px' }}>{formatDate(getToDate(selectedRequest))}</strong>
                                             </Col>
                                             <Col xs={6} md={3}>
                                                  <small className="text-muted d-block" style={{ fontSize: isMobile ? '11px' : '12px' }}>Total Days</small>
                                                  <Badge bg="info" className="mt-1" style={{ fontSize: isMobile ? '11px' : '12px' }}>
                                                       {selectedRequest.days_requested || selectedRequest.days || calculateDays(getFromDate(selectedRequest), getToDate(selectedRequest))} Days
                                                  </Badge>
                                             </Col>
                                             <Col xs={6} md={3}>
                                                  {selectedRequest.applied_by && (
                                                       <>
                                                            <small className="text-muted d-block" style={{ fontSize: isMobile ? '11px' : '12px' }}>Applied By</small>
                                                            <strong style={{ fontSize: isMobile ? '13px' : '14px' }}>{selectedRequest.applied_by}</strong>
                                                       </>
                                                  )}
                                             </Col>
                                        </Row>
                                   </div>

                                   {/* Leave Balance Information */}
                                   {selectedRequest.leave_type !== 'LOP' && (
                                        <>
                                             <hr className="my-3" />
                                             <h6 className="mb-2" style={{ fontSize: isMobile ? '14px' : '16px' }}>Leave Balance</h6>
                                             {(() => {
                                                  const balance = selectedEmployeeBalances.find(b => b.leave_type === selectedRequest.leave_type);

                                                  if (!balance) {
                                                       return (
                                                            <Alert variant="warning" className="mb-0 py-2" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                                 <strong>⚠️ No balance record found for {selectedRequest.leave_type}</strong>
                                                            </Alert>
                                                       );
                                                  }

                                                  const totalAllocated = parseFloat(balance.total_leaves || 0);
                                                  const used = parseFloat(balance.used_leaves || 0);
                                                  const remaining = parseFloat(balance.remaining_leaves || 0);

                                                  return (
                                                       <div className="p-3 bg-light rounded" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                            <Row className="mb-2 g-2">
                                                                 <Col xs={4}>
                                                                      <small className="text-muted">Total</small>
                                                                      <div><strong className={isMobile ? 'fs-6' : 'fs-5'}>{totalAllocated}</strong></div>
                                                                 </Col>
                                                                 <Col xs={4}>
                                                                      <small className="text-muted">Used</small>
                                                                      <div><strong className={`text-danger ${isMobile ? 'fs-6' : 'fs-5'}`}>{used}</strong></div>
                                                                 </Col>
                                                                 <Col xs={4}>
                                                                      <small className="text-muted">Remaining</small>
                                                                      <div><strong className={`text-success ${isMobile ? 'fs-6' : 'fs-5'}`}>{remaining}</strong></div>
                                                                 </Col>
                                                            </Row>
                                                            {selectedRequest.status?.toLowerCase() === 'pending' && (() => {
                                                                 const requestedDays = parseFloat(selectedRequest.days_requested || selectedRequest.days || calculateDays(getFromDate(selectedRequest), getToDate(selectedRequest)));
                                                                 const afterApproval = remaining - requestedDays;

                                                                 if (afterApproval < 0) {
                                                                      return (
                                                                           <Alert variant="danger" className="mt-2 mb-0 py-2" style={{ fontSize: isMobile ? '11px' : '12px' }}>
                                                                                <strong>⚠️ Warning:</strong> Insufficient balance!
                                                                           </Alert>
                                                                      );
                                                                 } else {
                                                                      return (
                                                                           <div className="p-2 bg-white rounded border mt-2">
                                                                                <small className="text-muted">After approval: </small>
                                                                                <strong className="text-primary">{afterApproval.toFixed(1)} days</strong>
                                                                           </div>
                                                                      );
                                                                 }
                                                            })()}
                                                       </div>
                                                  );
                                             })()}
                                        </>
                                   )}

                                   {selectedRequest.leave_type === 'LOP' && (
                                        <>
                                             <hr className="my-3" />
                                             <Alert variant="info" className="mb-0 py-2" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                  <strong>Loss of Pay (LOP)</strong> - Unlimited days available
                                             </Alert>
                                        </>
                                   )}

                                   <hr className="my-3" />
                                   <h6 className="mb-2" style={{ fontSize: isMobile ? '14px' : '16px' }}>Reason for Leave</h6>
                                   <div className="p-3 bg-light rounded" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                        {selectedRequest.reason || 'No reason provided'}
                                   </div>

                                   {selectedRequest.remarks && (
                                        <>
                                             <hr className="my-3" />
                                             <h6 className="mb-2" style={{ fontSize: isMobile ? '14px' : '16px' }}>Previous Remarks</h6>
                                             <div className="p-2 bg-warning bg-opacity-10 rounded border border-warning" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                  {selectedRequest.remarks}
                                             </div>
                                        </>
                                   )}

                                   {canApproveReject(selectedRequest) && (
                                        <>
                                             <hr className="my-3" />
                                             <h6 className="mb-2" style={{ fontSize: isMobile ? '14px' : '16px' }}>Action Required</h6>
                                             <Form.Group className="mb-2">
                                                  <Form.Label className="small" style={{ fontSize: isMobile ? '12px' : '13px' }}>Remarks</Form.Label>
                                                  <Form.Control
                                                       as="textarea"
                                                       rows={2}
                                                       id="adminRemarks"
                                                       placeholder="Enter your remarks"
                                                       size={isMobile ? 'sm' : 'md'}
                                                  />
                                             </Form.Group>
                                             <div className="d-flex gap-2">
                                                  <Button
                                                       variant="success"
                                                       size="sm"
                                                       onClick={() => {
                                                            const remarks = document.getElementById('adminRemarks').value;
                                                            handleApprove(selectedRequest.id, remarks);
                                                       }}
                                                       style={{ fontSize: isMobile ? '12px' : '13px', flex: isMobile ? '1' : 'auto' }}
                                                  >
                                                       <Check size={14} className="me-1" />
                                                       Approve
                                                  </Button>
                                                  <Button
                                                       variant="danger"
                                                       size="sm"
                                                       onClick={() => {
                                                            const remarks = document.getElementById('adminRemarks').value;
                                                            handleReject(selectedRequest.id, remarks);
                                                       }}
                                                       style={{ fontSize: isMobile ? '12px' : '13px', flex: isMobile ? '1' : 'auto' }}
                                                  >
                                                       <X size={14} className="me-1" />
                                                       Reject
                                                  </Button>
                                             </div>
                                        </>
                                   )}

                                   {canCancel(selectedRequest) && (
                                        <>
                                             <hr className="my-3" />
                                             <h6 className="mb-2" style={{ fontSize: isMobile ? '14px' : '16px' }}>Cancel Request</h6>
                                             <Form.Group className="mb-2">
                                                  <Form.Label className="small" style={{ fontSize: isMobile ? '12px' : '13px' }}>Cancellation Reason</Form.Label>
                                                  <Form.Control
                                                       as="textarea"
                                                       rows={2}
                                                       id="cancelReason"
                                                       placeholder="Enter cancellation reason"
                                                       required
                                                       isInvalid={!!cancelReasonError}
                                                       onChange={() => setCancelReasonError('')}
                                                       size={isMobile ? 'sm' : 'md'}
                                                  />
                                                  <Form.Control.Feedback type="invalid">
                                                       {cancelReasonError}
                                                  </Form.Control.Feedback>
                                             </Form.Group>
                                             <Button
                                                  variant="warning"
                                                  size="sm"
                                                  onClick={() => {
                                                       const reason = document.getElementById('cancelReason').value;
                                                       handleCancel(selectedRequest.id, reason);
                                                  }}
                                                  className={isMobile ? 'w-100' : ''}
                                                  style={{ fontSize: isMobile ? '12px' : '13px' }}
                                             >
                                                  Cancel Request
                                             </Button>
                                        </>
                                   )}
                              </>
                         )}
                    </Modal.Body>
               </Modal>
          </div>
     );
};

export default LeaveRequests;